class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => {
        if (evt.code === 'Escape') {
          this.close();
        }
      },
    );

    const overlay =
      this.querySelector(
        '#CartDrawer-Overlay',
      );

    if (overlay) {
      overlay.addEventListener(
        'click',
        this.close.bind(this),
      );
    }

    /*
     * ZIONIC:
     * Immediate visual feedback when customer
     * taps/clicks the remove button.
     *
     * We DO NOT replace Dawn's actual cart-removal
     * request. Shopify still remains responsible
     * for the real cart state.
     */
    this.addEventListener(
      'pointerdown',
      this.handleTrashPointerDown.bind(this),
      {
        passive: true,
      },
    );

    this.addEventListener(
      'pointerup',
      this.handleTrashPointerUp.bind(this),
      {
        passive: true,
      },
    );

    this.addEventListener(
      'pointercancel',
      this.handleTrashPointerUp.bind(this),
      {
        passive: true,
      },
    );

    this.addEventListener(
      'click',
      this.handleTrashClick.bind(this),
      true,
    );

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink =
      document.querySelector(
        '#cart-icon-bubble',
      );

    if (!cartLink) return;

    cartLink.setAttribute(
      'role',
      'button',
    );

    cartLink.setAttribute(
      'aria-haspopup',
      'dialog',
    );

    cartLink.addEventListener(
      'click',
      (event) => {
        event.preventDefault();

        this.open(cartLink);
      },
    );

    cartLink.addEventListener(
      'keydown',
      (event) => {
        if (
          event.code.toUpperCase() ===
          'SPACE'
        ) {
          event.preventDefault();

          this.open(cartLink);
        }
      },
    );
  }

  /*
   * ZIONIC
   * Detect the Dawn remove control.
   */
  getTrashButton(target) {
    if (!(target instanceof Element)) {
      return null;
    }

    return target.closest(
      'cart-remove-button .button',
    );
  }

  /*
   * Instant physical press feedback.
   */
  handleTrashPointerDown(event) {
    const button =
      this.getTrashButton(
        event.target,
      );

    if (!button) return;

    button.classList.add(
      'zionic-trash-pressed',
    );
  }

  handleTrashPointerUp(event) {
    const button =
      this.getTrashButton(
        event.target,
      );

    if (button) {
      button.classList.remove(
        'zionic-trash-pressed',
      );
    }

    /*
     * Also clear any button that may have lost
     * pointer capture between down/up.
     */
    this.querySelectorAll(
      '.zionic-trash-pressed',
    ).forEach((pressedButton) => {
      pressedButton.classList.remove(
        'zionic-trash-pressed',
      );
    });
  }

  /*
   * Optimistic visual removal.
   *
   * Dawn's native CartRemoveButton still performs
   * the real cart update. We only make the UI
   * respond instantly instead of looking frozen
   * during Shopify's network/render round trip.
   */
  handleTrashClick(event) {
    const button =
      this.getTrashButton(
        event.target,
      );

    if (!button) return;

    const cartItem =
      button.closest(
        '.cart-item',
      );

    if (!cartItem) return;

    cartItem.classList.add(
      'zionic-remove-pending',
    );

    button.classList.remove(
      'zionic-trash-pressed',
    );
  }

  /*
   * ZIONIC
   * Replays discount animations from zero.
   */
  replayDiscountAnimation() {
    this.classList.remove(
      'zionic-discount-replay',
    );

    void this.offsetWidth;

    this.classList.add(
      'zionic-discount-replay',
    );
  }

  /*
   * Notify promo popup immediately whenever
   * drawer opens or cart contents change.
   */
  dispatchZionicCartUpdate() {
    const detail = {
      source: 'cart-drawer',
      timestamp: Date.now(),
    };

    document.dispatchEvent(
      new CustomEvent(
        'zionic:cart-updated',
        {
          bubbles: true,
          detail,
        },
      ),
    );

    window.dispatchEvent(
      new CustomEvent(
        'zionic:cart-updated',
        {
          detail,
        },
      ),
    );
  }

  open(triggeredBy) {
    /*
     * Drawer already open.
     */
    if (
      this.classList.contains(
        'active',
      )
    ) {
      this.replayDiscountAnimation();
      this.dispatchZionicCartUpdate();

      return;
    }

    if (triggeredBy) {
      this.setActiveElement(
        triggeredBy,
      );
    }

    const cartDrawerNote =
      this.querySelector(
        '[id^="Details-"] summary',
      );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute(
        'role',
      )
    ) {
      this.setSummaryAccessibility(
        cartDrawerNote,
      );
    }

    /*
     * Begin opening on next animation frame.
     */
    requestAnimationFrame(() => {
      this.classList.add(
        'animate',
        'active',
      );

      requestAnimationFrame(() => {
        this.replayDiscountAnimation();
        this.dispatchZionicCartUpdate();
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          this.classList.contains(
            'is-empty',
          )
            ? this.querySelector(
                '.drawer__inner-empty',
              )
            : document.getElementById(
                'CartDrawer',
              );

        const focusElement =
          this.querySelector(
            '.drawer__inner',
          ) ||
          this.querySelector(
            '.drawer__close',
          );

        if (
          containerToTrapFocusOn &&
          focusElement
        ) {
          trapFocus(
            containerToTrapFocusOn,
            focusElement,
          );
        }
      },
      {
        once: true,
      },
    );

    document.body.classList.add(
      'overflow-hidden',
    );

    this.querySelector(
      'cart-drawer-items',
    )?.dispatchViewEvent();

    /*
     * One immediate promo state notification.
     */
    this.dispatchZionicCartUpdate();
  }

  close() {
    this.classList.remove(
      'active',
    );

    removeTrapFocus(
      this.activeElement,
    );

    document.body.classList.remove(
      'overflow-hidden',
    );

    this.classList.remove(
      'zionic-discount-replay',
    );

    /*
     * Clear temporary interaction states.
     */
    this.querySelectorAll(
      '.zionic-trash-pressed',
    ).forEach((button) => {
      button.classList.remove(
        'zionic-trash-pressed',
      );
    });

    this.dispatchZionicCartUpdate();
  }

  setSummaryAccessibility(
    cartDrawerNote,
  ) {
    cartDrawerNote.setAttribute(
      'role',
      'button',
    );

    cartDrawerNote.setAttribute(
      'aria-expanded',
      'false',
    );

    const controlledElement =
      cartDrawerNote.nextElementSibling;

    if (
      controlledElement &&
      controlledElement.getAttribute(
        'id',
      )
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        controlledElement.id,
      );
    }

    cartDrawerNote.addEventListener(
      'click',
      (event) => {
        const details =
          event.currentTarget.closest(
            'details',
          );

        event.currentTarget.setAttribute(
          'aria-expanded',
          details
            ? !details.hasAttribute(
                'open',
              )
            : false,
        );
      },
    );

    cartDrawerNote.parentElement
      ?.addEventListener(
        'keyup',
        onKeyUpEscape,
      );
  }

  renderContents(parsedState) {
    const drawerInner =
      this.querySelector(
        '.drawer__inner',
      );

    if (
      drawerInner?.classList.contains(
        'is-empty',
      )
    ) {
      drawerInner.classList.remove(
        'is-empty',
      );
    }

    this.productId =
      parsedState.id;

    this.getSectionsToRender().forEach(
      (section) => {
        const sectionElement =
          section.selector
            ? document.querySelector(
                section.selector,
              )
            : document.getElementById(
                section.id,
              );

        if (!sectionElement) return;

        const sectionHTML =
          parsedState.sections?.[
            section.id
          ];

        if (!sectionHTML) return;

        const newHTML =
          this.getSectionInnerHTML(
            sectionHTML,
            section.selector,
          );

        if (newHTML !== null) {
          sectionElement.innerHTML =
            newHTML;
        }
      },
    );

    /*
     * New HTML means any optimistic
     * .zionic-remove-pending state disappears
     * naturally with the old DOM.
     */
    this.dispatchZionicCartUpdate();

    /*
     * Rebind overlay because Dawn replaced
     * the cart drawer markup.
     */
    window.setTimeout(() => {
      const overlay =
        this.querySelector(
          '#CartDrawer-Overlay',
        );

      if (overlay) {
        overlay.addEventListener(
          'click',
          this.close.bind(this),
        );
      }

      this.open();
    }, 0);
  }

  getSectionInnerHTML(
    html,
    selector = '.shopify-section',
  ) {
    const parsedDocument =
      new DOMParser().parseFromString(
        html,
        'text/html',
      );

    const element =
      parsedDocument.querySelector(
        selector,
      );

    return element
      ? element.innerHTML
      : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(
    html,
    selector = '.shopify-section',
  ) {
    return new DOMParser()
      .parseFromString(
        html,
        'text/html',
      )
      .querySelector(
        selector,
      );
  }

  setActiveElement(element) {
    this.activeElement =
      element;
  }
}

customElements.define(
  'cart-drawer',
  CartDrawer,
);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define(
  'cart-drawer-items',
  CartDrawerItems,
);